import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { X, Clock, Copy, Check, ArrowRight } from 'lucide-react';

const COUPON_CODE = 'WELCOME50';
const COUNTDOWN_MINUTES = 15;
const SESSION_KEY = 'fsh_exit_shown';

function formatTime(totalSeconds) {
    const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return { m, s };
}

export default function ExitIntentModal() {
    const [visible, setVisible] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_MINUTES * 60);
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    const show = useCallback(() => {
        if (sessionStorage.getItem(SESSION_KEY)) return;
        sessionStorage.setItem(SESSION_KEY, '1');
        setVisible(true);
    }, []);

    // Exit-intent: mouse leaves viewport from the top
    useEffect(() => {
        const handler = (e) => {
            if (e.clientY <= 5 && e.relatedTarget === null) show();
        };
        document.addEventListener('mouseout', handler);
        return () => document.removeEventListener('mouseout', handler);
    }, [show]);

    // Mobile fallback: show after 45s of inactivity on the page
    useEffect(() => {
        const t = setTimeout(() => {
            if (!sessionStorage.getItem(SESSION_KEY)) show();
        }, 45000);
        return () => clearTimeout(t);
    }, [show]);

    // Countdown
    useEffect(() => {
        if (!visible) return;
        timerRef.current = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) { clearInterval(timerRef.current); return 0; }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [visible]);

    const handleCopy = () => {
        navigator.clipboard.writeText(COUPON_CODE).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClaim = () => {
        setVisible(false);
        navigate('/register');
    };

    if (!visible) return null;

    const { m, s } = formatTime(secondsLeft);
    const expired = secondsLeft <= 0;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-4" data-testid="exit-intent-overlay">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setVisible(false)} />

            <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-amber-500/30 bg-slate-950 shadow-[0_0_60px_rgba(212,175,55,0.15)]" data-testid="exit-intent-modal">
                {/* Close */}
                <button onClick={() => setVisible(false)} className="absolute top-3 right-3 z-10 text-slate-500 hover:text-slate-300 transition-colors" data-testid="exit-intent-close">
                    <X className="h-5 w-5" />
                </button>

                {/* Gold accent bar */}
                <div className="h-1 bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600" />

                <div className="px-6 pt-6 pb-7 text-center">
                    {/* Badge */}
                    <span className="inline-block px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/25 text-amber-400 text-[11px] font-bold uppercase tracking-wider mb-4">
                        Exclusive offer
                    </span>

                    {/* Headline */}
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-100 leading-tight mb-1">
                        Wait — don't leave yet!
                    </h2>
                    <p className="text-slate-400 text-sm mb-5">
                        Get <span className="text-amber-400 font-bold">50% off every month, for life</span> when you sign up now.
                    </p>

                    {/* Price comparison */}
                    <div className="flex items-center justify-center gap-4 mb-5">
                        <div className="text-slate-500">
                            <span className="text-lg line-through">£29.99</span>
                            <span className="text-xs block">/month</span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-amber-500" />
                        <div className="text-amber-400">
                            <span className="text-3xl font-black">£15.00</span>
                            <span className="text-xs block font-medium">/month forever</span>
                        </div>
                    </div>

                    {/* Coupon code */}
                    <div className="flex items-center justify-center gap-2 mb-5">
                        <div className="flex items-center bg-slate-900 border border-dashed border-amber-500/40 rounded-lg px-4 py-2.5">
                            <span className="text-amber-400 font-mono font-bold text-lg tracking-widest mr-3" data-testid="exit-intent-coupon">{COUPON_CODE}</span>
                            <button onClick={handleCopy} className="text-slate-400 hover:text-amber-400 transition-colors" data-testid="exit-intent-copy">
                                {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    {/* Countdown */}
                    {!expired ? (
                        <div className="flex items-center justify-center gap-2 mb-6" data-testid="exit-intent-timer">
                            <Clock className="h-4 w-4 text-rose-400" />
                            <span className="text-sm text-slate-400">Offer expires in</span>
                            <div className="flex items-center gap-1">
                                <span className="bg-slate-800 text-slate-100 font-mono font-bold text-sm rounded px-2 py-1">{m}</span>
                                <span className="text-slate-500 font-bold">:</span>
                                <span className="bg-slate-800 text-slate-100 font-mono font-bold text-sm rounded px-2 py-1">{s}</span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-rose-400 text-sm mb-6">This offer has expired.</p>
                    )}

                    {/* CTA */}
                    <Button onClick={handleClaim} disabled={expired}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-base h-12 disabled:opacity-40"
                        data-testid="exit-intent-claim">
                        Claim 50% Off Now
                    </Button>

                    <button onClick={() => setVisible(false)} className="mt-3 text-xs text-slate-600 hover:text-slate-400 transition-colors" data-testid="exit-intent-dismiss">
                        No thanks, I'll pay full price
                    </button>
                </div>
            </div>
        </div>
    );
}
